import { useCallback } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
    fetchConversationsPage,
    type ConversationRow,
    type ConversationsPageMeta,
    type ConversationsParams
} from '../services/conversations';
import { CONVERSATIONS_KEY, CONVERSATIONS_PAGE_KEY } from './queryKeys';
import { invalidateSidebarCounts } from './useSidebarCounts';

export function useServerConversations(params: ConversationsParams & { enabled?: boolean }) {
    const queryClient = useQueryClient();

    const queryKey = [
        ...CONVERSATIONS_PAGE_KEY,
        params.filter,
        params.page,
        params.limit,
        params.sort,
        params.order,
        params.team?.join(',') ?? '',
        params.assignee?.join(',') ?? '',
        params.practiceIds?.join(',') ?? '',
        params.status?.join(',') ?? '',
        params.search ?? ''
    ];

    const { data, isLoading, isFetching, isError } = useQuery({
        queryKey,
        queryFn: () => fetchConversationsPage(params),
        staleTime: 60 * 1000,
        refetchInterval: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
        refetchOnMount: 'always',
        placeholderData: keepPreviousData,
        enabled: params.enabled !== false
    });

    const refresh = useCallback(async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: [CONVERSATIONS_KEY] }),
            invalidateSidebarCounts(queryClient)
        ]);
    }, [queryClient]);

    return {
        conversations: (data?.data ?? []) as ConversationRow[],
        meta: (data?.meta ?? {
            total: 0,
            page: 1,
            limit: params.limit,
            totalPages: 1
        }) as ConversationsPageMeta,
        isLoading,
        isFetching,
        isError,
        refresh
    };
}
